import React from "react";
import { MessageCircle, Search, RefreshCw, Wifi, WifiOff, Flag } from "lucide-react";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { ScrollArea } from "../ui/scroll-area";
import { useChat } from "../../contexts/ChatContext";
import { useAuth } from "../../contexts/AuthContext";

export const MessagesSidebar = ({
  conversations,
  activeConversation,
  onSelectConversation,
  searchTerm,
  onSearchChange,
  unseenMessages,
  loading,
  onRefresh
}) => {
  const { isUserOnline, socket } = useChat();
  const { currentUser } = useAuth();

  // Format user name
  const getUserName = (user) => {
    if (user.name) return user.name;
    if (user.firstName && user.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    if (user.firstName) return user.firstName;
    if (user.email) return user.email.split('@')[0];
    return 'Unknown User';
  };

  // Get user avatar
  const getUserAvatar = (user) => {
    return user.image || user.avatar || '';
  };

  // Get user initials
  const getUserInitials = (user) => {
    const name = getUserName(user);
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Format timestamp
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 1) return 'now';
    if (diffMins < 60) return `${diffMins}m`;
    if (diffHours < 24) return `${diffHours}h`;
    if (diffDays < 7) return `${diffDays}d`;
    
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Get unseen count for user - FIXED to properly use unseenMessages prop
  const getUnseenCount = (userId) => {
    const count = unseenMessages?.[userId] || 0;
    console.log(`Getting unseen count for user ${userId}:`, count);
    return count;
  };

  // Truncate last message
  const truncateMessage = (message, maxLength = 50) => {
    if (!message) return 'No messages yet';
    if (message.length <= maxLength) return message;
    return message.substring(0, maxLength) + '...';
  };


  // Debug socket connection
  console.log("Socket connection status:", {
    connected: socket?.connected,
    socketId: socket?.id,
    currentUser: currentUser?.id || currentUser?._id,
    unseenMessages
  });

  return (
    <div className="col-span-4 bg-white border-r-0 md:border-r border-gray-200 flex flex-col h-full">
      {/* Header */}
      <div className="p-4 md:p-6 border-b border-gray-200 bg-gradient-to-r from-igbanwo-mint/5 to-emerald-50 flex-shrink-0">
        <div className="flex items-center justify-between mb-3 md:mb-4">
          <div className="flex items-center space-x-2">
            <h1 className="text-lg md:text-xl font-semibold text-gray-900">Messages</h1>
            {/* Connection Status */}
            <div className="flex items-center">
              {socket?.connected ? (
                <Wifi className="w-4 h-4 text-green-500" />
              ) : (
                <WifiOff className="w-4 h-4 text-red-500" />
              )}
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onRefresh}
            disabled={loading}
            className="h-8 w-8 p-0"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
        
        {/* Search Input */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <Input
            placeholder="Search conversations..."
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            className="pl-10 bg-white border-gray-200 focus:border-igbanwo-mint text-sm"
          />
        </div>
      </div>

      {/* Conversations List */}
      <div className="flex-1 overflow-hidden"> 
        <ScrollArea className="h-[500px]">
          {loading && conversations.length === 0 ? (
            <div className="p-6 md:p-8 text-center">
              <div className="animate-spin w-6 h-6 md:w-8 md:h-8 border-2 border-igbanwo-mint border-t-transparent rounded-full mx-auto mb-3"></div>
              <p className="text-gray-500 text-sm md:text-base">Loading conversations...</p>
            </div>
          ) : conversations.length > 0 ? (
            conversations.map((conversation) => {
              const userId = conversation.id || conversation._id;
              const isActive = activeConversation === userId;
              const isOnline = isUserOnline(userId);
              const unseenCount = getUnseenCount(userId);

              return (
                <div
                  key={userId}
                  className={`relative group p-3 md:p-4 border-b border-gray-100 hover:bg-igbanwo-mint/10 cursor-pointer transition-all duration-200 ${
                    isActive
                      ? "bg-igbanwo-mint/20 border-l-4 border-l-igbanwo-mint"
                      : ""
                  }`}
                >
                  <div 
                    onClick={() => onSelectConversation(userId)}
                    className="flex items-center space-x-2 md:space-x-3"
                  >
                    {/* Avatar with Online Status */}
                    <div className="relative flex-shrink-0">
                      <Avatar className="w-10 h-10 md:w-12 md:h-12">
                        <AvatarImage
                          src={getUserAvatar(conversation)}
                          alt={getUserName(conversation)}
                          className="w-full h-full object-cover"
                        />
                        <AvatarFallback className="bg-gradient-to-br from-igbanwo-mint/20 to-emerald-100 text-igbanwo-mint text-xs md:text-sm font-medium">
                          {getUserInitials(conversation)}
                        </AvatarFallback>
                      </Avatar>
                      {isOnline && (
                        <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 md:w-3.5 md:h-3.5 bg-green-500 border-2 border-white rounded-full"></div>
                      )}
                    </div>

                    {/* Conversation Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <p className={`text-sm md:text-base truncate ${unseenCount > 0 ? 'font-semibold' : 'font-medium'}`}>
                          {getUserName(conversation)}
                        </p>
                        <span className="text-xs text-gray-500 flex-shrink-0 ml-2">
                          {formatTimestamp(conversation.lastMessageTime)}
                        </span>
                      </div>
                      <p className={`text-xs md:text-sm truncate ${unseenCount > 0 ? 'text-gray-900 font-medium' : 'text-gray-600'}`}>
                        {truncateMessage(conversation.lastMessage?.text || conversation.lastMessage)}
                      </p>
                    </div>

                    {/* Unseen Messages Badge */}
                    {unseenCount > 0 && (
                      <div className="w-5 h-5 bg-igbanwo-mint text-white text-xs rounded-full flex items-center justify-center font-medium flex-shrink-0">
                        {unseenCount > 9 ? '9+' : unseenCount}
                      </div>
                    )}
                  </div>

                  
                </div>
              );
            })
          ) : (
            <div className="p-6 md:p-8 text-center">
              <MessageCircle className="w-10 h-10 md:w-12 md:h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 mb-2 text-sm md:text-base">No conversations yet</p>
              <p className="text-xs md:text-sm text-gray-400">
                Start sharing food to connect with your community!
              </p>
            </div>
          )}
        </ScrollArea>
      </div>

      {/* Debug Info - Remove in production */}
      {process.env.NODE_ENV === 'development' && (
        <div className="p-2 border-t border-gray-200 bg-gray-50">
          <div className="text-xs text-gray-600">
            <div>Socket: {socket?.connected ? 'Connected' : 'Disconnected'}</div>
            <div>Users: {conversations.length}</div>
            <div>Current User: {currentUser?.firstName || 'None'}</div>
            <div>Unseen Messages: {JSON.stringify(unseenMessages)}</div>
          </div>
        </div>
      )}
    </div>
  );
};